// Single source of truth for "where is the backend" and "where is the site".
//
// On the WEB the app is served same-origin by Express, so API_ORIGIN is ''
// and every existing relative '/api/...' call is untouched.
//
// Under CAPACITOR (Android/iOS app) the bundle is served from
// capacitor://localhost / https://localhost, so relative URLs would hit the
// app shell instead of the server — API_ORIGIN must be the real backend.
//
// Native detection uses the window.Capacitor global the runtime injects, so
// this file adds no dependency to the web bundle.

import { appEnv } from "@/utils/seoEnv";

export const IS_NATIVE =
  typeof window !== "undefined" &&
  !!window.Capacitor &&
  typeof window.Capacitor.isNativePlatform === "function" &&
  window.Capacitor.isNativePlatform();

export const API_ORIGIN = IS_NATIVE
  ? import.meta.env.VITE_API_ORIGIN || "https://renants.com"
  : "";

// Canonical public origin for OUTWARD-FACING links (share links, the
// post-publish redirect). It has to follow the environment the app is really
// running in: a listing published on the dev box used to send the poster to
// renants.com, a different server where that listing does not exist.
//
//   production  -> https://renants.com
//   development -> https://finoptima.tech
//   local       -> the browser's own origin (localhost:3000 / :5173)
//
// The environment comes from <meta name="app-env">, which the backend stamps
// into every served index.html from its own NODE_ENV (modules/seoMeta.js).
// That is the only trustworthy signal — vue-app/.env is committed with
// VITE_NODE_ENV=production, so every build claims to be production no matter
// where it was made. See vue-app/src/utils/seoEnv.ts.
const WEB_ORIGIN_BY_ENV = {
  production: "https://renants.com",
  development: "https://finoptima.tech",
};

function resolveWebOrigin() {
  // Lets a deployment override the mapping without a code change (a second dev
  // host, a preview environment).
  const override = import.meta.env.VITE_WEB_ORIGIN;
  if (override) return String(override).trim().replace(/\/+$/, "");

  // Native app: index.html comes from the bundle, so there is no app-env tag
  // to read. Follow whichever backend this build was pointed at — never
  // window.location.origin, which is capacitor://localhost.
  if (IS_NATIVE) return API_ORIGIN || WEB_ORIGIN_BY_ENV.production;

  const mapped = WEB_ORIGIN_BY_ENV[appEnv()];
  if (mapped) return mapped;

  // NODE_ENV=local, or no tag at all (the Vite dev server serves index.html
  // itself, so nothing stamps it). The browser is already on the right host,
  // so its own origin is the correct base for the link.
  return typeof window !== "undefined" && window.location
    ? window.location.origin
    : WEB_ORIGIN_BY_ENV.production;
}

export const WEB_ORIGIN = resolveWebOrigin();
